import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import GameMarqueeBackground from "@/components/GameMarqueeBackground";
import RisingBubbles from "@/components/RisingBubbles";
import AnimatedNumber from "@/components/AnimatedNumber";
import Sparkline from "@/components/Sparkline";
import { Button } from "@/components/ui/button";
import {
    ArrowRight,
    MessageCircle,
    Gamepad2,
    Eye,
    Trophy,
    Waves,
    TrendingUp,
    Activity,
} from "lucide-react";

const Home = () => {
    const [summary, setSummary] = useState(null);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [s, g] = await Promise.all([
                    apiClient.get("/games/summary"),
                    apiClient.get("/games"),
                ]);
                if (!mounted) return;
                setSummary(s.data);
                setGames(g.data.games || []);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        const t = setInterval(async () => {
            try {
                const s = await apiClient.get("/games/summary");
                setSummary(s.data);
            } catch {
                /* ignore */
            }
        }, 30000);
        return () => {
            mounted = false;
            clearInterval(t);
        };
    }, []);

    const allTimePeak = useMemo(
        () => games.reduce((s, g) => s + (g.peakCcu || 0), 0),
        [games]
    );

    return (
        <div data-testid="home-page" className="pt-16">
            {/* -------- Asymmetric hero -------- */}
            <section className="relative overflow-hidden">
                <GameMarqueeBackground games={games} />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-24 pb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        {/* Left column — wordmark stacked, tagline, CTAs */}
                        <div className="lg:col-span-7 fade-up">
                            <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.28em] text-teal-300 px-3 py-1.5 rounded-full border border-teal-500/30 bg-teal-500/10 backdrop-blur-md">
                                <span className="live-dot" /> Roblox Game Studio
                            </span>
                            <h1 className="mt-6 font-display font-bold tracking-tight text-white leading-[1.02] drop-shadow-[0_4px_30px_rgba(0,0,0,0.9)]">
                                <span className="block text-6xl sm:text-7xl lg:text-8xl">Deep</span>
                                <span className="block text-6xl sm:text-7xl lg:text-8xl text-shimmer mt-2">
                                    Current<span className="text-white/40">.</span>
                                </span>
                            </h1>
                            <p className="mt-6 font-display text-xl md:text-2xl text-slate-300 font-light max-w-xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.9)]">
                                Games that pull you under.
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <Button
                                    asChild
                                    size="lg"
                                    data-testid="hero-cta-games"
                                    className="cta-glow rounded-full bg-teal-500 hover:bg-teal-400 text-black font-semibold h-12 px-6"
                                >
                                    <Link to="/games">
                                        <Gamepad2 className="w-4 h-4" /> Check out our games
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </Button>
                                <Button
                                    asChild
                                    size="lg"
                                    variant="outline"
                                    data-testid="hero-cta-dm"
                                    className="rounded-full h-12 px-6 border-white/20 bg-white/5 backdrop-blur-md text-white hover:bg-white/10 hover:text-white"
                                >
                                    <Link to="/about#contact">
                                        <MessageCircle className="w-4 h-4" /> DM us
                                    </Link>
                                </Button>
                            </div>
                        </div>

                        {/* Right column — clean live stats card, no distracting overlay */}
                        <div className="lg:col-span-5 fade-up" style={{ animationDelay: "150ms" }}>
                            <div className="relative rounded-2xl border border-teal-500/20 bg-black/70 backdrop-blur-xl p-6 md:p-7 overflow-hidden shadow-[0_0_60px_-30px_rgba(45,212,191,0.6)]">
                                {/* Soft radial pulse behind the number — subtle, no chaos */}
                                <div
                                    aria-hidden="true"
                                    className="absolute -inset-1 opacity-70"
                                    style={{
                                        background:
                                            "radial-gradient(80% 60% at 20% 30%, rgba(45,212,191,0.10) 0%, transparent 60%)",
                                    }}
                                />
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-300">
                                            <span className="live-dot" /> Live · Roblox
                                        </div>
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                                            updates 30s
                                        </div>
                                    </div>

                                    <div className="mt-6">
                                        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">
                                            Playing right now
                                        </div>
                                        <div
                                            data-testid="stat-total-playing"
                                            className="font-display font-black text-6xl md:text-7xl text-teal-400 leading-none mt-2"
                                        >
                                            {loading ? "—" : <AnimatedNumber value={summary?.totalPlaying ?? 0} />}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-2 font-mono">
                                            concurrent · across our portfolio
                                        </div>
                                    </div>

                                    <div className="mt-6 grid grid-cols-2 gap-3">
                                        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-mono">
                                                <Eye className="w-3 h-3" /> Total visits
                                            </div>
                                            <div
                                                data-testid="stat-total-visits"
                                                className="font-display font-black text-2xl text-white mt-1"
                                            >
                                                {loading ? "—" : <AnimatedNumber value={summary?.totalVisits ?? 0} />}
                                            </div>
                                        </div>
                                        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-mono">
                                                <Gamepad2 className="w-3 h-3" /> Live titles
                                            </div>
                                            <div
                                                data-testid="stat-game-count"
                                                className="font-display font-black text-2xl kw-violet mt-1"
                                            >
                                                {loading ? "—" : <AnimatedNumber value={summary?.gameCount ?? 0} format={(n) => n} />}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* -------- Peak CCU banner — rising bubbles, on-brand for "Deep Current" -------- */}
            <section
                data-testid="peak-ccu-section"
                className="relative overflow-hidden py-20 md:py-24 border-y border-white/5 bg-black"
            >
                <RisingBubbles density={1.6} />
                <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
                    <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.28em] text-violet-300 px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 backdrop-blur-md">
                        <Trophy className="w-3.5 h-3.5" /> Studio Record
                    </div>
                    <h2 className="mt-6 font-display text-3xl md:text-5xl font-bold text-white leading-tight">
                        All-time <span className="kw-teal">peak CCU</span>
                    </h2>
                    <div className="mt-8 flex flex-col items-center">
                        <Waves className="w-9 h-9 text-teal-400" strokeWidth={2.2} />
                        <div
                            data-testid="stat-all-time-peak"
                            className="mt-3 font-display font-black text-6xl md:text-7xl text-white"
                        >
                            {loading ? "—" : (
                                <>
                                    <AnimatedNumber value={allTimePeak} />+
                                </>
                            )}
                        </div>
                        <div className="mt-3 text-sm text-slate-400 font-medium max-w-md">
                            Concurrent players riding the current across every Deep Current title.
                        </div>
                    </div>
                </div>
            </section>

            {/* -------- Bento stats: "Results in numbers" -------- */}
            <section
                data-testid="results-section"
                className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-28 pb-8"
            >
                <div className="max-w-2xl mb-12 fade-up">
                    <span className="text-[11px] font-mono uppercase tracking-[0.28em] text-slate-400">
                        By the numbers
                    </span>
                    <h2 className="mt-3 font-display text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.02] tracking-tight">
                        Results in <span className="kw-teal">numbers</span>.
                    </h2>
                    <p className="mt-4 text-slate-400 text-base md:text-lg leading-relaxed">
                        We build, acquire, and grow Roblox experiences — aligning devs, scaling live-ops, and stacking stats across the portfolio.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* 01 — VISITS (tall left card) */}
                    <div
                        data-testid="bento-visits"
                        className="bento-card md:row-span-2 rounded-3xl border border-white/10 bg-black/60 p-8 md:p-10 relative min-h-[420px] fade-up"
                        style={{ animationDelay: "0ms" }}
                    >
                        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-slate-500">
                            01 — Visits
                        </div>
                        <div className="mt-10 font-display font-black text-6xl md:text-8xl text-white leading-none">
                            {loading ? "—" : <><AnimatedNumber value={summary?.totalVisits ?? 0} />+</>}
                        </div>
                        <p className="mt-5 text-slate-400 text-sm md:text-base max-w-sm leading-relaxed">
                            Lifetime visits across every experience we manage. A testament to growth at scale.
                        </p>
                        <Link
                            to="/games"
                            className="arrow-link mt-6 inline-flex items-center gap-1.5 text-sm text-teal-300 hover:text-teal-200 font-medium"
                        >
                            Explore the portfolio <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 opacity-90">
                            <Sparkline
                                points={[10, 15, 22, 30, 45, 70, 110, 180, 280, 400]}
                                width={800}
                                height={200}
                                strokeColor="#2dd4bf"
                                fillColor="rgba(45, 212, 191, 0.22)"
                                duration={2000}
                                delay={300}
                                className="w-full h-full"
                            />
                        </div>
                    </div>

                    {/* 02 — GAMES (top right) */}
                    <div
                        data-testid="bento-games"
                        className="bento-card rounded-3xl border border-white/10 bg-black/60 p-8 relative min-h-[200px] fade-up"
                        style={{ animationDelay: "100ms" }}
                    >
                        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-slate-500">
                            02 — Games
                        </div>
                        <div className="mt-6 flex items-baseline gap-2 font-display font-black text-5xl md:text-6xl text-white leading-none">
                            <span className="text-teal-400/70 text-4xl">~</span>
                            {loading ? "—" : <AnimatedNumber value={summary?.gameCount ?? 0} format={(n) => n} />}
                        </div>
                        <p className="mt-4 text-slate-400 text-sm max-w-xs leading-relaxed">
                            Games actively managed by Deep Current.
                        </p>
                        <Link
                            to="/games"
                            className="arrow-link mt-4 inline-flex items-center gap-1.5 text-sm text-teal-300 hover:text-teal-200 font-medium"
                        >
                            Learn more <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        {/* Floating mini icons */}
                        {games.slice(0, 3).map((g, i) => (
                            <img
                                key={g.placeId}
                                src={g.icon}
                                alt=""
                                aria-hidden="true"
                                className="absolute float-slow rounded-lg border border-white/10 w-14 h-14 object-cover shadow-lg"
                                style={{
                                    top: `${18 + i * 10}px`,
                                    right: `${20 + i * 60}px`,
                                    animationDelay: `${i * 300}ms`,
                                    zIndex: 3 - i,
                                }}
                            />
                        ))}
                    </div>

                    {/* 03 — LIVE (middle right) */}
                    <div
                        data-testid="bento-live"
                        className="bento-card rounded-3xl border border-white/10 bg-black/60 p-8 relative min-h-[200px] fade-up"
                        style={{ animationDelay: "200ms" }}
                    >
                        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-slate-500">
                            03 — Live
                        </div>
                        <div className="mt-6 flex items-center gap-3">
                            <span className="live-dot" />
                            <div className="font-display font-black text-5xl md:text-6xl text-white leading-none">
                                {loading ? "—" : <><AnimatedNumber value={summary?.totalPlaying ?? 0} />+</>}
                            </div>
                        </div>
                        <p className="mt-4 text-slate-400 text-sm max-w-xs leading-relaxed">
                            Concurrent players right now across our portfolio.
                        </p>
                        <Link
                            to="/games"
                            className="arrow-link mt-4 inline-flex items-center gap-1.5 text-sm text-teal-300 hover:text-teal-200 font-medium"
                        >
                            Learn more <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        <div className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 w-40 h-24 opacity-80">
                            <Sparkline
                                points={[5, 8, 12, 9, 14, 20, 16, 24, 28, 22, 30]}
                                width={200}
                                height={100}
                                strokeColor="#a78bfa"
                                fillColor="rgba(167, 139, 250, 0.18)"
                                duration={1500}
                                delay={500}
                                className="w-full h-full"
                            />
                        </div>
                    </div>

                    {/* 04 — PEAK (bottom left) */}
                    <div
                        data-testid="bento-peak"
                        className="bento-card rounded-3xl border border-white/10 bg-black/60 p-8 relative min-h-[200px] fade-up"
                        style={{ animationDelay: "300ms" }}
                    >
                        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-slate-500">
                            04 — Peak record
                        </div>
                        <div className="mt-6 flex items-baseline gap-2 font-display font-black text-5xl md:text-6xl text-white leading-none">
                            <TrendingUp className="w-6 h-6 text-teal-400" />
                            {loading ? "—" : <><AnimatedNumber value={allTimePeak} />+</>}
                        </div>
                        <p className="mt-4 text-slate-400 text-sm max-w-xs leading-relaxed">
                            All-time concurrent-player record across every title.
                        </p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-violet-300 font-medium">
                            <Activity className="w-3.5 h-3.5" /> Studio record
                        </span>
                    </div>

                    {/* 05 — Work with us CTA (bottom right) */}
                    <div
                        data-testid="bento-cta"
                        className="bento-card rounded-3xl border border-teal-500/20 bg-gradient-to-br from-black via-[#031a19] to-black p-8 relative min-h-[200px] overflow-hidden fade-up"
                        style={{ animationDelay: "400ms" }}
                    >
                        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.28em] text-teal-300">
                            <Waves className="w-3.5 h-3.5" /> Deep Current
                        </div>
                        <h3 className="mt-6 font-display text-3xl md:text-4xl font-bold text-white leading-tight max-w-sm">
                            Looking to work with us?
                        </h3>
                        <div className="mt-6 flex items-center gap-3">
                            <Button
                                asChild
                                data-testid="bento-cta-contact"
                                className="rounded-full bg-teal-500 hover:bg-teal-400 text-black font-semibold h-10 px-5"
                            >
                                <Link to="/about#contact">
                                    Contact us <ArrowRight className="w-4 h-4" />
                                </Link>
                            </Button>
                        </div>
                        {/* Decorative game icons floating on right */}
                        {games.slice(0, 4).map((g, i) => (
                            <img
                                key={g.placeId}
                                src={g.icon}
                                alt=""
                                aria-hidden="true"
                                className="absolute float-slow rounded-lg border border-white/10 w-16 h-16 object-cover shadow-lg"
                                style={{
                                    bottom: `${-20 + i * 12}px`,
                                    right: `${-16 + i * 44}px`,
                                    animationDelay: `${i * 350}ms`,
                                    zIndex: 4 - i,
                                    transform: `rotate(${(i - 1.5) * 6}deg)`,
                                }}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Home no longer includes a games grid — full list lives on /games */}
        </div>
    );
};

export default Home;
